import React from 'react';
import { Card, Button, Tag, Empty, Tooltip } from 'antd';
import {
  FileTextOutlined, SafetyCertificateOutlined, StopOutlined,
  PrinterOutlined, FolderOpenOutlined, EyeOutlined, DownloadOutlined,
  FilePdfOutlined, BulbOutlined, CheckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

/**
 * Centre de téléchargement des documents officiels du dossier.
 * Tout est généré à la volée - l'utilisateur peut imprimer / sauver en PDF
 * via le navigateur depuis chaque page.
 */
export default function OngletDocuments({ sinistre, navigate }) {
  const { t } = useTranslation('dossierSinistre');
  if (!sinistre) return null;
  const id = sinistre.sinistreTrackingId;
  const garantieAcquise = sinistre.statut === 'GARANTIE_CONFIRMEE'
    || sinistre.statut === 'ATTENTE_PV' || sinistre.statut === 'ATTENTE_RC'
    || sinistre.statut === 'MUR' || sinistre.statut === 'OFFRE_EFFECTUE'
    || sinistre.statut === 'BAP' || sinistre.statut === 'SOLDE';
  const garantieRefusee = sinistre.statut === 'GARANTIE_NON_ACQUISE';

  const docs = [
    {
      key: 'fiche',
      titre: t('ongletDocuments.fiche.titre'),
      description: t('ongletDocuments.fiche.description'),
      icon: <FileTextOutlined style={{ fontSize: 28, color: '#0369a1' }} />,
      bg: '#eff6ff',
      border: '#bfdbfe',
      disponible: true,
      raison: t('ongletDocuments.fiche.raison'),
      onClick: () => navigate(`/sinistres/${id}/fiche`),
    },
    {
      key: 'attestation',
      titre: t('ongletDocuments.attestation.titre'),
      description: t('ongletDocuments.attestation.description'),
      icon: <SafetyCertificateOutlined style={{ fontSize: 28, color: '#15803d' }} />,
      bg: '#dcfce7',
      border: '#86efac',
      disponible: garantieAcquise,
      raison: garantieAcquise
        ? t('ongletDocuments.attestation.raisonDisponible')
        : t('ongletDocuments.attestation.raisonIndisponible'),
      onClick: () => navigate(`/sinistres/${id}/attestation`),
    },
    {
      key: 'non-garantie',
      titre: t('ongletDocuments.nonGarantie.titre'),
      description: t('ongletDocuments.nonGarantie.description'),
      icon: <StopOutlined style={{ fontSize: 28, color: '#b91c1c' }} />,
      bg: '#fef2f2',
      border: '#fecaca',
      disponible: garantieRefusee,
      raison: garantieRefusee
        ? t('ongletDocuments.nonGarantie.raisonDisponible')
        : t('ongletDocuments.nonGarantie.raisonIndisponible'),
      onClick: () => navigate(`/sinistres/${id}/non-garantie`),
    },
    {
      key: 'ged',
      titre: t('ongletDocuments.ged.titre'),
      description: t('ongletDocuments.ged.description'),
      icon: <FolderOpenOutlined style={{ fontSize: 28, color: '#7c3aed' }} />,
      bg: '#f5f3ff',
      border: '#ddd6fe',
      disponible: true,
      raison: t('ongletDocuments.ged.raison'),
      onClick: () => navigate(`/ged?sinistreId=${id}`),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f9fafb', borderRadius: 10, fontSize: 12.5, color: '#6b7280' }}>
        <BulbOutlined style={{ marginRight: 6, color: '#f59e0b' }} />{t('ongletDocuments.aide')}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
      }}>
        {docs.map(doc => (
          <Card key={doc.key}
            style={{
              borderRadius: 12,
              opacity: doc.disponible ? 1 : 0.55,
              border: `4px solid ${doc.disponible ? doc.border : '#d1d5db'}`,
              cursor: doc.disponible ? 'pointer' : 'default',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            hoverable={doc.disponible}
            bodyStyle={{ padding: '18px 20px' }}
            onClick={doc.disponible ? doc.onClick : undefined}
          >
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 12,
                background: doc.disponible ? doc.bg : '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {doc.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                  {doc.titre}
                </div>
                {doc.disponible ? (
                  <Tag color="green" style={{ borderRadius: 10, fontSize: 10 }}>
                    <CheckOutlined style={{ marginRight: 4 }} />{t('ongletDocuments.disponible')}
                  </Tag>
                ) : (
                  <Tag style={{ borderRadius: 10, fontSize: 10 }}>
                    {t('ongletDocuments.nonDisponible')}
                  </Tag>
                )}
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5, marginBottom: 10 }}>
              {doc.description}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginBottom: 14 }}>
              {doc.raison}
            </div>
            {doc.disponible && (
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="small" type="primary" icon={<EyeOutlined />}
                  style={{ background: '#1a6b3c', borderColor: '#1a6b3c', flex: 1 }}
                  onClick={(e) => { e.stopPropagation(); doc.onClick(); }}>
                  {t('ongletDocuments.ouvrir')}
                </Button>
                <Tooltip title={t('ongletDocuments.imprimer')}>
                  <Button size="small" icon={<PrinterOutlined />}
                    onClick={(e) => { e.stopPropagation(); doc.onClick(); }}>
                  </Button>
                </Tooltip>
              </div>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}
